import React from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { DashboardCalendar } from '../../shared/ui-models/occupancy-dashboard.ui-model';
import { Card, ChartTip, EmptyRow, Metric, Pill } from './components';
import { DashboardPace, PacePoint, pct } from './engine';
import { C, fmtNum, fmtPct, occTone } from './theme';

export interface PaceTabProps {
  pace: DashboardPace;
  paceMonth: number;
  setPaceMonth: (m: number) => void;
  cal: DashboardCalendar;
}

const FORWARD_HEADERS = [
  'Month',
  'Days out',
  'On the books',
  'Nights sold',
  'Sellable',
  'Typical share by now',
  'Implied final',
  'Read'
];

const readOf = (implied: number | null): { read: string; tone: string } => {
  if (implied === null) return { read: '—', tone: C.muted };
  if (implied >= 70) return { read: 'Strong', tone: C.green };
  if (implied >= 45) return { read: 'On track', tone: C.blue };
  if (implied >= 25) return { read: 'Soft', tone: '#9A6700' };
  return { read: 'Weak', tone: '#B42318' };
};

export function PaceTab({ pace, paceMonth, setPaceMonth, cal }: PaceTabProps) {
  const m = pace.target;
  const atToday = m.daysOut > 0 ? pace.curve.find((c) => c.lead <= m.daysOut) : null;

  return (
    <div className="mt-5 space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs" style={{ color: C.muted }}>
          Target month
        </span>
        <select
          value={paceMonth}
          onChange={(e) => setPaceMonth(Number(e.target.value))}
          className="rounded-md border bg-white px-3 py-1.5 text-sm"
          style={{ borderColor: C.line }}
        >
          {pace.monthStats.map((s) => (
            <option key={s.mi} value={s.mi}>
              {s.long}
              {s.isPast ? ' · closed' : s.isCurrent ? ' · in progress' : ' · future'}
            </option>
          ))}
        </select>
        <p className="text-xs" style={{ color: C.muted }}>
          {'On-the-books occupancy for '}
          {m.long}
          {' as it stood at each point before the month began.'}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Metric
          label={m.isPast ? 'Final occupancy' : 'On the books today'}
          value={fmtPct(m.todayOcc)}
          sub={`${fmtNum(m.final)} of ${fmtNum(m.sellable)} sellable nights`}
          accent={C.green}
        />
        <Metric
          label="At month start"
          value={fmtPct(pct(m.onBooks[0], m.sellable))}
          sub={
            m.final ? `${((m.onBooks[0] / m.final) * 100).toFixed(0)} % of where it landed` : '—'
          }
        />
        <Metric
          label="30 days out"
          value={fmtPct(pct(m.onBooks[30], m.sellable))}
          sub={`${fmtNum(m.onBooks[30])} nights on the books`}
        />
        <Metric
          label="60 days out"
          value={fmtPct(pct(m.onBooks[60], m.sellable))}
          sub={`${fmtNum(m.onBooks[60])} nights on the books`}
        />
        <Metric
          label={m.isPast ? 'Picked up in month' : 'Days to month start'}
          value={m.isPast ? fmtNum(m.final - m.onBooks[0]) : `${Math.max(0, m.daysOut)}`}
          sub={
            m.isPast
              ? 'Nights booked after the month opened'
              : atToday
              ? `Typical by now: ${fmtPct(atToday.typicalShare, 0)} of final`
              : 'In progress'
          }
          accent={C.clay}
        />
      </div>
      <Card
        title={`Pickup curve — ${m.long}`}
        note="Solid line is this month. Dotted line is the shape closed months followed, rebased to this month's outcome."
      >
        <div className="h-72 px-2 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={pace.curve.slice().reverse()}
              margin={{ top: 5, right: 20, bottom: 5, left: -10 }}
            >
              <CartesianGrid stroke="#EEF1F6" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: C.muted }}
                axisLine={{ stroke: C.line }}
                tickLine={false}
              />
              <YAxis
                unit="%"
                tick={{ fontSize: 11, fill: C.muted }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={
                  <ChartTip
                    rows={(p: PacePoint) => [
                      { k: 'On the books', v: fmtPct(p.occ), color: C.green },
                      { k: 'Nights', v: fmtNum(p.nights) },
                      { k: 'Share of final', v: fmtPct(p.shareOfFinal, 0) },
                      { k: 'Typical share', v: fmtPct(p.typicalShare, 0), color: C.muted }
                    ]}
                  />
                }
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="occ"
                name="On the books"
                stroke={C.green}
                strokeWidth={2.5}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="typicalOcc"
                name="Typical shape"
                stroke={C.muted}
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card
        title="Forward book"
        note="Where the months ahead stand today against the shape closed months followed."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b" style={{ borderColor: C.line, background: '#FBFCFE' }}>
              <tr>
                {FORWARD_HEADERS.map((h, i) => (
                  <th
                    key={h}
                    className={`px-4 py-3 text-[11px] font-semibold ${
                      i === 0 || i === 2 || i === 7 ? 'text-left' : 'text-right'
                    }`}
                    style={{ color: C.muted }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pace.forward.map((f) => {
                const implied = f.impliedFinalOcc;
                const { read, tone } = readOf(implied);
                return (
                  <tr key={f.mi} className="border-b last:border-0" style={{ borderColor: C.line }}>
                    <td className="px-4 py-3 font-medium">
                      {f.long}
                      {f.isCurrent && (
                        <span className="ml-2 text-xs" style={{ color: C.muted }}>
                          in progress
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{f.daysOut}</td>
                    <td className="px-4 py-3">
                      <Pill value={f.todayOcc} tone={occTone} />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtNum(f.final)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtNum(f.sellable)}</td>
                    <td className="px-4 py-3 text-right tabular-nums" style={{ color: C.muted }}>
                      {fmtPct(f.expectedShare, 0)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtPct(implied, 0)}</td>
                    <td className="px-4 py-3 text-xs font-medium" style={{ color: tone }}>
                      {read}
                    </td>
                  </tr>
                );
              })}
              {pace.forward.length === 0 && <EmptyRow cols={8} />}
            </tbody>
          </table>
        </div>
      </Card>
      <Card
        title="Every month, at a glance"
        note="On the books at each lead point, as a share of sellable nights. A dot means that point has not been reached yet."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b" style={{ borderColor: C.line, background: '#FBFCFE' }}>
              <tr>
                <th
                  className="px-4 py-3 text-left text-[11px] font-semibold"
                  style={{ color: C.muted }}
                >
                  Month
                </th>
                {pace.LEADS.map((l) => (
                  <th
                    key={l}
                    className="px-3 py-3 text-right text-[11px] font-semibold"
                    style={{ color: C.muted }}
                  >
                    {l === 0 ? 'Start' : `${l}d`}
                  </th>
                ))}
                <th
                  className="px-4 py-3 text-right text-[11px] font-semibold"
                  style={{ color: C.muted }}
                >
                  Final
                </th>
              </tr>
            </thead>
            <tbody>
              {pace.monthStats.map((s) => (
                <tr key={s.mi} className="border-b last:border-0" style={{ borderColor: C.line }}>
                  <td className="whitespace-nowrap px-4 py-2.5 font-medium">
                    {s.long}
                    {!s.isPast && (
                      <span className="ml-2 text-xs" style={{ color: C.muted }}>
                        {s.isCurrent ? 'in progress' : 'future'}
                      </span>
                    )}
                  </td>
                  {pace.LEADS.map((l) => {
                    const reached = s.first - l <= cal.today;
                    return (
                      <td
                        key={l}
                        className="px-3 py-2.5 text-right text-xs tabular-nums"
                        style={{ color: reached ? C.ink : '#C9CFD9' }}
                      >
                        {reached ? fmtPct(pct(s.onBooks[l], s.sellable), 0) : '·'}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2.5 text-right">
                    {s.isPast ? (
                      <Pill value={s.finalOcc} tone={occTone} />
                    ) : (
                      <span className="text-xs" style={{ color: C.muted }}>
                        open
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <div
        className="rounded-lg border bg-white p-4 text-xs"
        style={{ borderColor: C.line, color: C.muted }}
      >
        Pace is reconstructed from each reservation&rsquo;s creation date. A canceled reservation
        counts as on the books from the day it was created until the day it was canceled, and never
        toward a month&rsquo;s final occupancy. Cancellations with no recorded cancellation date
        &mdash; older ones, and those that arrived through the channel manager &mdash; are missing,
        so historic pace is still slightly flattered.
      </div>
    </div>
  );
}
